import React, { useState, useEffect } from "react";
import { adminAPI } from "../services/api";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    designation: "",
    mobile: "",
    pin: "123456",
    is_staff: false, // Added is_staff field
  });
  const [error, setError] = useState(null);

  const [pagePermissions, setPagePermissions] = useState({
    azure_vms: false,
    email_control: false,
    storage_volumes: false,
    user_management: false,
    device_control: false,
  });

  const [vmPermissions, setVmPermissions] = useState([]);
  const [availableVMs, setAvailableVMs] = useState([]);
  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAGCUsers();
      setUsers(response.data.results || response.data);
    } catch (err) {
      console.error("Failed to load AGC users", err);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadAvailableVMs = async () => {
    try {
      const response = await adminAPI.getAvailableVMs();
      setAvailableVMs(response.data.results || response.data || []);
    } catch (err) {
      console.error("Failed to load Azure VMs", err);
      setAvailableVMs([]);
    }
  };
  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setCurrentUserId(null);
    setFormData({
      username: "",
      password: "",
      first_name: "",
      last_name: "",
      designation: "",
      mobile: "",
      pin: "123456",
      is_staff: false, // Default to subordinate user
    });
        setPagePermissions({
      azure_vms: false,
      email_control: false,
      storage_volumes: false,
      user_management: false,
      device_control: false,
    });

    setVmPermissions([]);
    loadAvailableVMs();
    setError(null);
    setShowModal(true);
  };

    const handleOpenEditModal = async (user) => {
    setIsEditing(true);
    setCurrentUserId(user.id);

    setFormData({
      username: user.username,
      password: "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      designation: user.designation || "",
      mobile: user.mobile || "",
      pin: user.pin || "123456",
      is_staff: user.is_staff || false,
    });

    setError(null);

    // Reset permissions first
    setPagePermissions({
      azure_vms: false,
      email_control: false,
      storage_volumes: false,
      user_management: false,
      device_control: false,
    });

    setVmPermissions([]);

    // Load available Azure VMs
    await loadAvailableVMs();

    // Load permissions assigned to this user
    try {
      const response = await adminAPI.getAGCPermissions(user.id);

      const data = response.data;

      setPagePermissions({
        azure_vms: data.pages?.azure_vms || false,
        email_control: data.pages?.email_control || false,
        storage_volumes: data.pages?.storage_volumes || false,
        user_management: data.pages?.user_management || false,
        device_control: data.pages?.device_control || false,
      });

      setVmPermissions(data.azure_vms || []);
    } catch (err) {
      console.error("Failed to load user permissions", err);
    }

    setShowModal(true);
  };

    const handleSaveUser = async (e) => {
    e.preventDefault();

    try {
      let userId = currentUserId;

      if (isEditing) {
        await adminAPI.updateAGCUser(currentUserId, formData);
      } else {
        const response = await adminAPI.createAGCUser(formData);

        userId =
          response.data?.id ||
          response.data?.user?.id ||
          response.data?.data?.id;
      }

      if (!userId) {
        throw new Error(
          "User was saved, but the user ID could not be determined."
        );
      }

      await adminAPI.saveAGCPermissions({
        user_id: userId,
        pages: pagePermissions,
        azure_vms: vmPermissions,
      });

      setShowModal(false);
      loadUsers();
    } catch (err) {
      console.error("Failed to save user permissions", err);

      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to save user"
      );
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await adminAPI.deleteAGCUser(id);
        loadUsers();
      } catch (err) {
        console.error("Failed to delete user", err);
      }
    }
  };

  const handleToggle = async (id) => {
    await adminAPI.toggleAGCUserAccess(id);
    loadUsers();
  };

  return React.createElement(
    "div",
    { className: "p-6" },
    React.createElement(
      "div",
      { className: "flex justify-between items-center mb-6" },
      React.createElement(
        "h2",
        { className: "text-xl font-bold text-gray-800" },
        "User Account Management",
      ),
      React.createElement(
        "button",
        {
          onClick: handleOpenCreateModal,
          className:
            "bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-semibold",
        },
        "+ Create New User",
      ),
    ),

    React.createElement(
      "div",
      {
        className:
          "bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden",
      },
      React.createElement(
        "table",
        { className: "w-full text-left border-collapse" },
        React.createElement(
          "thead",
          null,
          React.createElement(
            "tr",
            {
              className:
                "bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase",
            },
            React.createElement("th", { className: "p-4" }, "Username"),
            React.createElement("th", { className: "p-4" }, "Full Name"),
            React.createElement("th", { className: "p-4" }, "Role"), // Added Role Header
            React.createElement("th", { className: "p-4" }, "Status"),
            React.createElement(
              "th",
              { className: "p-4 text-right" },
              "Actions",
            ),
          ),
        ),
        React.createElement(
          "tbody",
          { className: "divide-y divide-gray-100 text-sm" },
          users.map((user) =>
            React.createElement(
              "tr",
              { key: user.id, className: "hover:bg-gray-50" },
              React.createElement(
                "td",
                { className: "p-4 font-medium text-gray-900" },
                user.username,
              ),
              React.createElement(
                "td",
                { className: "p-4 text-gray-600" },
                `${user.first_name || ""} ${user.last_name || ""}`,
              ),
              React.createElement(
                // Added Role Display
                "td",
                { className: "p-4" },
                React.createElement(
                  "span",
                  {
                    className: `px-2.5 py-1 rounded-full text-xs font-semibold ${user.is_staff ? "bg-purple-50 text-purple-600" : "bg-gray-100 text-gray-600"}`,
                  },
                  user.is_staff ? "Admin" : "Standard User",
                ),
              ),
              React.createElement(
                "td",
                { className: "p-4" },
                React.createElement(
                  "span",
                  {
                    className: `px-2.5 py-1 rounded-full text-xs font-semibold ${user.is_enabled ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`,
                  },
                  user.is_enabled ? "Active" : "Disabled",
                ),
              ),
              React.createElement(
                "td",
                { className: "p-4 text-right space-x-2" },
                React.createElement(
                  "button",
                  {
                    onClick: () => handleToggle(user.id),
                    className: `px-2.5 py-1 rounded text-xs font-medium ${user.is_enabled ? "bg-amber-50 text-amber-600 hover:bg-amber-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`,
                  },
                  user.is_enabled ? "Disable" : "Enable",
                ),
                React.createElement(
                  "button",
                  {
                    onClick: () => handleOpenEditModal(user),
                    className:
                      "px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100",
                  },
                  "Edit",
                ),
                React.createElement(
                  "button",
                  {
                    onClick: () => handleDelete(user.id),
                    className:
                      "px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100",
                  },
                  "Delete",
                ),
              ),
            ),
          ),
        ),
      ),
    ),

    showModal &&
      React.createElement(
        "div",
        {
          className:
            "fixed inset-0 bg-black/50 flex items-center justify-center z-50",
        },
        React.createElement(
          "div",
          {
  className:
    "bg-white p-6 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl",
},
          React.createElement(
            "h3",
            { className: "text-lg font-bold mb-4" },
            isEditing ? "Modify User Account" : "Create New User",
          ),
          error &&
            React.createElement(
              "p",
              { className: "text-red-500 text-xs mb-3" },
              error,
            ),
          React.createElement(
            "form",
            { onSubmit: handleSaveUser, className: "space-y-3" },
            React.createElement("input", {
              type: "text",
              placeholder: "Username",
              value: formData.username,
              onChange: (e) =>
                setFormData({ ...formData, username: e.target.value }),
              className: "w-full p-2 border rounded text-sm",
              required: true,
            }),
            React.createElement("input", {
              type: "password",
              placeholder: isEditing ? "New Password (optional)" : "Password",
              value: formData.password,
              onChange: (e) =>
                setFormData({ ...formData, password: e.target.value }),
              className: "w-full p-2 border rounded text-sm",
              required: !isEditing,
            }),
            React.createElement("input", {
              type: "text",
              maxLength: "6",
              placeholder: "6-Digit Clearance PIN",
              value: formData.pin,
              onChange: (e) =>
                setFormData({ ...formData, pin: e.target.value }),
              className: "w-full p-2 border rounded text-sm",
              required: true,
            }),
            React.createElement(
              "div",
              { className: "grid grid-cols-2 gap-2" },
              React.createElement("input", {
                type: "text",
                placeholder: "First Name",
                value: formData.first_name,
                onChange: (e) =>
                  setFormData({ ...formData, first_name: e.target.value }),
                className: "w-full p-2 border rounded text-sm",
              }),
              React.createElement("input", {
                type: "text",
                placeholder: "Last Name",
                value: formData.last_name,
                onChange: (e) =>
                  setFormData({ ...formData, last_name: e.target.value }),
                className: "w-full p-2 border rounded text-sm",
              }),
            ),
            React.createElement("input", {
              type: "text",
              placeholder: "Designation",
              value: formData.designation,
              onChange: (e) =>
                setFormData({ ...formData, designation: e.target.value }),
              className: "w-full p-2 border rounded text-sm",
            }),
            React.createElement("input", {
              type: "text",
              placeholder: "Mobile",
              value: formData.mobile,
              onChange: (e) =>
                setFormData({ ...formData, mobile: e.target.value }),
              className: "w-full p-2 border rounded text-sm",
            }),

            // --- NEW: Admin Privileges Checkbox ---
            React.createElement(
              "div",
              { className: "flex items-center space-x-2 mt-2 py-2 border-t" },
              React.createElement("input", {
                type: "checkbox",
                checked: formData.is_staff,
                onChange: (e) =>
                  setFormData({ ...formData, is_staff: e.target.checked }),
                className:
                  "w-4 h-4 text-slate-800 rounded focus:ring-slate-800 cursor-pointer",
              }),
              React.createElement(
                "label",
                {
                  className:
                    "text-sm font-semibold text-gray-700 cursor-pointer",
                  onClick: () =>
                    setFormData({ ...formData, is_staff: !formData.is_staff }),
                },
                "Grant Admin Privileges (Allows User Management)",
              ),
            ),
            // ---------------------------------------

            React.createElement(
              "div",
              { className: "flex justify-end space-x-2 mt-4" },
                          // --- Page Access Permissions ---
            React.createElement(
              "div",
              {
                className:
                  "mt-3 pt-3 border-t max-h-72 overflow-y-auto",
              },
              React.createElement(
                "h4",
                { className: "text-sm font-bold text-gray-800 mb-2" },
                "Page Access",
              ),

              React.createElement(
                "p",
                { className: "text-xs text-gray-500 mb-3" },
                "Select which system pages this user can access.",
              ),

              [
                ["azure_vms", "Azure VM Management"],
                ["email_control", "Email Control"],
                ["storage_volumes", "Storage Volumes"],
                ["user_management", "User Management"],
                ["device_control", "Device Control"],
              ].map(([pageKey, pageName]) =>
                React.createElement(
                  "label",
                  {
                    key: pageKey,
                    className:
                      "flex items-center space-x-2 py-1.5 cursor-pointer",
                  },
                  React.createElement("input", {
                    type: "checkbox",
                    checked: pagePermissions[pageKey],
                    onChange: (e) =>
                      setPagePermissions({
                        ...pagePermissions,
                        [pageKey]: e.target.checked,
                      }),
                    className:
                      "w-4 h-4 text-slate-800 rounded focus:ring-slate-800",
                  }),
                  React.createElement(
                    "span",
                    { className: "text-sm text-gray-700" },
                    pageName,
                  ),
                ),
              ),
            ),
            // --------------------------------
                          // --- Azure VM Permissions ---
            pagePermissions.azure_vms &&
              React.createElement(
                "div",
                {
                  className:
                    "mt-3 pt-3 border-t",
                },
                React.createElement(
                  "h4",
                  { className: "text-sm font-bold text-gray-800 mb-2" },
                  "Azure VM Access",
                ),

                React.createElement(
                  "p",
                  { className: "text-xs text-gray-500 mb-3" },
                  "Choose which Azure VMs this user can access and which actions they can perform.",
                ),

                availableVMs.length === 0
                  ? React.createElement(
                      "p",
                      { className: "text-xs text-gray-500" },
                      "No Azure VMs available.",
                    )
                  : availableVMs.map((vm) => {
                      const existingPermission = vmPermissions.find(
                        (permission) =>
                          permission.vm_name === vm.name &&
                          permission.resource_group === vm.resource_group,
                      );

                      const canView =
                        existingPermission?.can_view || false;

                      const canStart =
                        existingPermission?.can_start || false;

                      const canStop =
                        existingPermission?.can_stop || false;

                      const updateVMPermission = (field, value) => {
                        const keyMatches = (permission) =>
                          permission.vm_name === vm.name &&
                          permission.resource_group === vm.resource_group;

                        const existing = vmPermissions.find(keyMatches);

                        if (existing) {
                          setVmPermissions(
                            vmPermissions.map((permission) =>
                              keyMatches(permission)
                                ? {
                                    ...permission,
                                    [field]: value,
                                  }
                                : permission,
                            ),
                          );
                        } else {
                          setVmPermissions([
                            ...vmPermissions,
                            {
                              vm_name: vm.name,
                              resource_group:
                                vm.resource_group || "",
                              can_view:
                                field === "can_view"
                                  ? value
                                  : false,
                              can_start:
                                field === "can_start"
                                  ? value
                                  : false,
                              can_stop:
                                field === "can_stop"
                                  ? value
                                  : false,
                            },
                          ]);
                        }
                      };

                      return React.createElement(
                        "div",
                        {
                          key: `${vm.name}-${vm.resource_group}`,
                          className:
                            "border rounded-lg p-3 mb-2 bg-gray-50",
                        },

                        React.createElement(
                          "div",
                          { className: "mb-2" },
                          React.createElement(
                            "p",
                            {
                              className:
                                "text-sm font-semibold text-gray-800",
                            },
                            vm.name,
                          ),
                          React.createElement(
                            "p",
                            {
                              className:
                                "text-xs text-gray-500",
                            },
                            `Resource Group: ${
                              vm.resource_group || "N/A"
                            }`,
                          ),
                        ),

                        React.createElement(
                          "div",
                          {
                            className:
                              "flex items-center space-x-4",
                          },

                          React.createElement(
                            "label",
                            {
                              className:
                                "flex items-center space-x-1 text-xs cursor-pointer",
                            },
                            React.createElement("input", {
                              type: "checkbox",
                              checked: canView,
                              onChange: (e) =>
                                updateVMPermission(
                                  "can_view",
                                  e.target.checked,
                                ),
                            }),
                            "View",
                          ),

                          React.createElement(
                            "label",
                            {
                              className:
                                "flex items-center space-x-1 text-xs cursor-pointer",
                            },
                            React.createElement("input", {
                              type: "checkbox",
                              checked: canStart,
                              onChange: (e) =>
                                updateVMPermission(
                                  "can_start",
                                  e.target.checked,
                                ),
                            }),
                            "Start",
                          ),

                          React.createElement(
                            "label",
                            {
                              className:
                                "flex items-center space-x-1 text-xs cursor-pointer",
                            },
                            React.createElement("input", {
                              type: "checkbox",
                              checked: canStop,
                              onChange: (e) =>
                                updateVMPermission(
                                  "can_stop",
                                  e.target.checked,
                                ),
                            }),
                            "Stop",
                          ),
                        ),
                      );
                    }),
              ),
            ),
                        // --------------------------------

            React.createElement(
              "div",
              { className: "flex justify-end space-x-2 mt-4" },

              React.createElement(
                "button",
                {
                  type: "button",
                  onClick: () => setShowModal(false),
                  className:
                    "px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded",
                },
                "Cancel",
              ),

              React.createElement(
                "button",
                {
                  type: "submit",
                  className:
                    "px-4 py-2 text-sm bg-slate-800 text-white rounded hover:bg-slate-900",
                },
                isEditing ? "Update Changes" : "Save User",
              ),
            ),
          ),
        ),
      ),
  );
};

export default UserManagement;
